/**
 * banner-slideshow.js
 * Banner principal con swipe nativo (scroll-snap del track, sin
 * reimplementar el gesto táctil). Flechas/dots/autoplay son controles
 * que operan sobre ese mismo scroll. Auto-init por IntersectionObserver,
 * scoped a `this`.
 */
if (!customElements.get('banner-slideshow')) {
  class BannerSlideshow extends HTMLElement {
    connectedCallback() {
      this._io = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          this.init();
        },
        { rootMargin: '200px' }
      );
      this._io.observe(this);

      // Red de seguridad: es el banner principal, probablemente above
      // the fold, así que casi siempre el observer dispara de inmediato.
      // El fallback cubre el caso de que no se posicione donde se espera.
      this._fallbackTimer = setTimeout(() => {
        if (!this._initialized) this.init();
      }, 2000);
    }

    disconnectedCallback() {
      this._io?.disconnect();
      clearTimeout(this._fallbackTimer);
      clearInterval(this._autoplayTimer);
      this._track?.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
    }

    init() {
      if (this._initialized) return;
      this._initialized = true;
      clearTimeout(this._fallbackTimer);

      this._track = this.querySelector('.cb-slideshow__track');
      this._slides = Array.from(this.querySelectorAll('.cb-slideshow__slide'));
      this._dotsWrap = this.querySelector('.cb-slideshow__dots');
      this._prevBtn = this.querySelector('.cb-slideshow__arrow--prev');
      this._nextBtn = this.querySelector('.cb-slideshow__arrow--next');

      if (!this._track || this._slides.length <= 1) return;

      this._current = 0;
      this._autoplay = this.dataset.autoplay === 'true';
      this._speed = parseInt(this.dataset.speed, 10) || 5000;
      this._recentInteraction = false;
      this._recentInteractionTimer = null;

      this.bindDots();
      this.bindArrows();
      this.bindScrollSync();
      this.startAutoplay();

      // Pausa el autoplay si la pestaña no está visible (ahorra ciclos
      // y evita animar contenido fuera de pantalla, buen hábito de INP).
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          clearInterval(this._autoplayTimer);
        } else {
          this.startAutoplay();
        }
      });
    }

    goTo(index) {
      const target = this._slides[(index + this._slides.length) % this._slides.length];
      if (!target) return;
      // Importante: NO usar target.scrollIntoView() aquí. scrollIntoView
      // puede arrastrar el scroll VERTICAL de toda la página para "traer
      // a la vista" el slide (sobre todo si el usuario está lejos, como
      // en el footer, durante el autoplay) — eso es justo lo que causaba
      // que el autoplay subiera la página entera. scrollTo() sobre el
      // propio track solo mueve el scroll horizontal interno del slider,
      // sin tocar la posición vertical del documento en absoluto.
      this._track.scrollTo({
        left: target.offsetLeft - this._track.offsetLeft,
        behavior: 'smooth',
      });
      this.setActive(index);
      this.markRecentInteraction();
    }

    setActive(index) {
      this._current = (index + this._slides.length) % this._slides.length;
      this._slides.forEach((s, i) => s.classList.toggle('is-active', i === this._current));
      if (this._dotsWrap) {
        this._dotsWrap
          .querySelectorAll('.cb-slideshow__dot')
          .forEach((d, i) => d.classList.toggle('is-active', i === this._current));
      }
    }

    markRecentInteraction() {
      this._recentInteraction = true;
      clearTimeout(this._recentInteractionTimer);
      this._recentInteractionTimer = setTimeout(() => {
        this._recentInteraction = false;
      }, 500);
      this.restartAutoplay();
    }

    bindDots() {
      if (!this._dotsWrap) return;
      this._dotsWrap.querySelectorAll('.cb-slideshow__dot').forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i));
      });
    }

    bindArrows() {
      if (this._prevBtn) this._prevBtn.addEventListener('click', () => this.goTo(this._current - 1));
      if (this._nextBtn) this._nextBtn.addEventListener('click', () => this.goTo(this._current + 1));
    }

    // El swipe de dedo lo resuelve scroll-snap nativamente (CSS). Aquí
    // solo escuchamos el resultado de ese scroll para mantener dots y
    // estado `is-active` sincronizados, sin reimplementar touchstart/
    // touchend ni cálculos de gesto.
    bindScrollSync() {
      this._onScroll = () => {
        clearTimeout(this._scrollTimer);
        this._scrollTimer = setTimeout(() => {
          if (this._recentInteraction) return;
          const trackLeft = this._track.getBoundingClientRect().left;
          let closestIdx = 0;
          let closestDist = Infinity;
          this._slides.forEach((s, i) => {
            const dist = Math.abs(s.getBoundingClientRect().left - trackLeft);
            if (dist < closestDist) {
              closestDist = dist;
              closestIdx = i;
            }
          });
          this.setActive(closestIdx);
        }, 100);
      };
      this._track.addEventListener('scroll', this._onScroll, { passive: true });
    }

    startAutoplay() {
      if (!this._autoplay || this._slides.length <= 1) return;
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = setInterval(() => this.goTo(this._current + 1), this._speed);
    }

    restartAutoplay() {
      if (!this._autoplay) return;
      clearInterval(this._autoplayTimer);
      this.startAutoplay();
    }
  }

  customElements.define('banner-slideshow', BannerSlideshow);
}